import Form, { FormSchema } from '@sezenta/antd-schema-form';
import { useConnection, useUser } from '@sezenta/connection';
import { Avatar, Button, Modal, notification, Popconfirm } from 'antd';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { User } from '../../dto';
import { useBusinessContext } from '../../context/BusinessContext';
import { DeleteOutlined, UserOutlined } from '@ant-design/icons';
import { ProfileSkeleton } from './ProfileSkeleton';

const schema1: FormSchema = [
  {
    id: 'firstName',
    type: 'string',
    name: '',
    props: { placeholder: 'First Name' },
    layout: { sm: 12 },
    options: {
      rules: [{ required: true, message: 'First Name is required' }],
    },
  },
  {
    id: 'lastName',
    type: 'string',
    name: '',
    props: { placeholder: 'Last Name' },
    layout: { sm: 12 },
    options: {
      rules: [{ required: true, message: 'Last Name is required' }],
    },
  },
];

const schema2: FormSchema = [
  {
    id: 'email',
    type: 'email',
    name: '',
    props: { placeholder: 'Email', disabled: true },
  },
];

const ProfileSettings = () => {
  const [form] = Form.useForm();
  const user = useUser();
  const connection = useConnection();
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [loadingSaving, setLoadingSaving] = useState<boolean>(false);
  const [picture, setPicture] = useState();
  const [file, setFile] = useState(null);
  const { businessId, select, roles } = useBusinessContext();
  const [userData, setUserData] = useState<User>();

  const getUserDetail = useCallback(async () => {
    try {
      setLoading(true);
      const response = await connection.get(`users/${user.userId}`);
      setUserData(response.data);
      form.setFieldsValue({
        firstName: response.data.firstName,
        lastName: response.data.lastName,
        email: response.data.email,
        picture: response.data.picture,
      });
      setLoading(false);
    } catch (e) {
      console.log(e);
      setLoading(false);
    }
  }, [connection, form, user.userId]);

  useEffect(() => {
    if (user) {
      getUserDetail().then();
    }
  }, [user, getUserDetail]);

  const onFinish = useCallback(
    async (values: any) => {
      console.log(values);
      setLoadingSaving(true);
      try {
        const res = await connection.patch(
          `users/${businessId}/${user.userId}`,
          {
            firstName: values.firstName,
            lastName: values.lastName,
            picture: values.picture,
            email: values.email,
          },
        );
        notification.success({
          message: 'Profile Updated',
          description:
            'Your profile information has been updated successfully.',
          placement: 'topRight',
        });
      } catch {
        console.log('user', values);
      }
      setLoadingSaving(false);
    },
    [businessId, connection, user.userId],
  );

  const schema3: FormSchema = useMemo(
    () => [
      {
        id: 'picture',
        // name: 'Update Profile Picture',
        type: 'upload',
        props: {
          placeholder: 'Picture',
          image: { width: 128, height: 128, crop: true },
          acceptSize: 5,
        },
      },
    ],
    [],
  );

  const showModal = () => {
    setOpen(true);
  };

  const handleOk = () => {
    setOpen(false);
  };

  const handleCancel = useCallback(() => {
    setOpen(false);
  }, []);

  const handleDelete = useCallback(async () => {
    if (userData) {
      const temp: User = { ...userData };
      temp.picture = null;
      setUserData(temp);
      form.setFieldsValue({
        picture: null,
      });
    }
  }, [form, userData]);

  const handleImageChange = useCallback(
    async (values: any) => {
      console.log({ values });
      if (userData && values.picture) {
        const temp: User = { ...userData };
        temp.picture = values.picture;
        setUserData(temp);
      }
    },
    [userData],
  );
  if (loading) {
    return <ProfileSkeleton />;
  }

  return (
    <div className="w-ful h-full">
      <div className="flex flex-col">
        <p className="font-semibold text-[16px]">Profile</p>
        <p className="text-gray-500">
          Manage your personal information and keep it up to date for a seamless
          experience
        </p>
      </div>
      <Form
        form={form}
        onFinish={onFinish}
        onValuesChange={handleImageChange}
        className="mt-7 w-full md:w-[70%] lg:w-[50%]"
      >
        <div className="overflow-x-auto">
          <table className="w-full min-w-[500px]">
            <tr>
              <td className="flex items-start font-semibold pr-4 py-2">
                Profile Picture
              </td>
              <td className="py-2">
                <div className="flex items-center mb-5">
                  <Avatar
                    src={userData?.picture}
                    icon={<UserOutlined />}
                    alt="profile"
                    className="w-16 h-16 rounded-[10px]"
                  />
                  <Button
                    onClick={handleDelete}
                    type="text"
                    className="ml-3 text-gray-500"
                  >
                    Remove
                  </Button>

                  <Button
                    type="text"
                    className="ml-3 text-primary-base"
                    onClick={showModal}
                  >
                    Change
                  </Button>
                </div>
              </td>
            </tr>
            <tr>
              <td className="flex items-start font-semibold pr-4 py-2">
                First & Last Name
              </td>
              <td className="py-2">
                <Form.Items schema={schema1} />
              </td>
            </tr>
            <tr>
              <td className="flex items-start font-semibold pr-4 py-2">Email</td>
              <td className="py-2">
                <Form.Items schema={schema2} />
              </td>
            </tr>
          </table>
        </div>
        <Button
          loading={loadingSaving}
          disabled={loadingSaving}
          type="primary"
          htmlType="submit"
          className="mt-5"
        >
          Update
        </Button>
        <Modal
          open={open}
          title={'Change Profile Picture'}
          footer={
            <Button type="primary" onClick={handleOk}>
              Done
            </Button>
          }
          centered
          onCancel={handleCancel}
          className={'fw-full p-10'}
        >
          <Form.Items schema={schema3} />
        </Modal>
      </Form>
    </div>
  );
};

export default ProfileSettings;
